import React, { createContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext(null);

const STORAGE_KEY = 'graspd-theme';
const THEMES = ['light', 'dark', 'system'];

function getSystemTheme() {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function getStoredTheme() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return THEMES.includes(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getStoredTheme);
  // In-memory-only override used by pages that need to force a light or dark
  // *appearance* (e.g. the login/paywall screens, which are deliberately
  // light-only) without touching the user's stored preference. Unlike
  // setTheme, this never reads/writes localStorage, so a hard reload on one
  // of those pages can't clobber the stored preference — see setThemeOverride.
  const [themeOverride, setThemeOverride] = useState(null);
  const [resolvedTheme, setResolvedTheme] = useState(() =>
    themeOverride || (theme === 'system' ? getSystemTheme() : theme)
  );

  // Apply theme to document
  useEffect(() => {
    const resolved = themeOverride || (theme === 'system' ? getSystemTheme() : theme);
    setResolvedTheme(resolved);

    document.documentElement.setAttribute('data-theme', resolved);
    document.documentElement.style.colorScheme = resolved;

    // Also set class for potential Tailwind dark mode usage
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(resolved);
  }, [theme, themeOverride]);

  // Listen for system theme changes when in 'system' mode
  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      // An active override is forcing the appearance — don't fight it.
      if (themeOverride) return;
      const newTheme = e.matches ? 'dark' : 'light';
      setResolvedTheme(newTheme);
      document.documentElement.setAttribute('data-theme', newTheme);
      document.documentElement.classList.remove('light', 'dark');
      document.documentElement.classList.add(newTheme);
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [theme, themeOverride]);

  const setTheme = useCallback((newTheme) => {
    if (!THEMES.includes(newTheme)) return;
    setThemeState(newTheme);
    try {
      localStorage.setItem(STORAGE_KEY, newTheme);
    } catch {}
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  }, [resolvedTheme, setTheme]);

  // Force resolvedTheme (and the document's data-theme) to 'light' | 'dark'
  // without persisting anything — pass null to clear the override and fall
  // back to the user's real preference. Safe to call from an effect cleanup.
  const applyThemeOverride = useCallback((forced) => {
    setThemeOverride(forced === 'light' || forced === 'dark' ? forced : null);
  }, []);

  const value = {
    theme,           // 'light' | 'dark' | 'system'
    resolvedTheme,   // 'light' | 'dark' (actual applied theme, override-aware)
    setTheme,
    toggleTheme,
    setThemeOverride: applyThemeOverride,
    isDark: resolvedTheme === 'dark',
    isLight: resolvedTheme === 'light',
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export default ThemeContext;
